import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseUpsertComponent } from '@cl/core/components';
import { DocumentsService } from '@cl/core/services/documents.service';
import { CommandResult, Document, DocumentType, EntityType, ErrorCode } from '@cl/shared/models';
import { DocumentFile } from '@cl/shared/models/documents/document-file.model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { DATE_TIME_FORMAT } from '@cl/shared/constants';
import { filter, lastValueFrom, map, Observable, Subscription, take } from 'rxjs';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { DocumentTypesService } from '@cl/core/services/document-types.service';
import moment from 'moment';
import { PayoutsService } from '@cl/core/services/payouts.service';
import { PerformersService } from '@cl/core/services';
import { Payout } from '@cl/shared/models/payouts/payout.model';
import { Performer } from '@cl/shared/models/performers/performer.model';

@Component({
    selector: 'cl-upsert-document',
    templateUrl: './upsert-document.component.html',
    styleUrl: './upsert-document.component.less',
    standalone: false,
})
export class UpsertDocumentComponent extends BaseUpsertComponent<Document, DocumentsService> implements OnInit {
    SearchFieldModelType = EntityType;
    GroupPermissions = GroupPermissionsType;
    EntityType = EntityType;

    dateTimeFormat = DATE_TIME_FORMAT;

    documentIsSignable: boolean;

    canEditFields: boolean = false;

    documentTypes$: Observable<DocumentType[]>;

    scontractId: number;
    contractId: number;
    performerId: number;
    fuelReportId: number;

    visibleCheckboxes: string[] = [];

    searchBetweenRevenuePayouts = (searchText: string): Promise<CommandResult<Payout[]>> => {
        return this.payoutsService.searchBetweenRevenuePayouts(searchText).toPromise();
    };

    searchBetweenPerformersRelatedToUser = async (searchText: string): Promise<CommandResult<Performer[]>> => {
        return await lastValueFrom(this.performersService.searchBetweenPerformersRelatedToUser(searchText));
    };

    documentOwnershipInputs: string[] = ['contractId', 'scontractId', 'workId', 'crmEventId', 'fuelReportId', 'payoutId', 'performerId'];
    visibleDocumentOwnershipInputs: string[] = [];

    constructor(
        protected documentsService: DocumentsService,
        protected messageService: NzMessageService,
        private modal: NzModalRef,
        private fb: UntypedFormBuilder,
        private documentTypesService: DocumentTypesService,
        private payoutsService: PayoutsService,
        private performersService: PerformersService,
    ) {
        super(documentsService, messageService);

        // set modal texts
        this.saveSuccessText = 'Dokuments saglabāts!';
        this.createObjectSaveErrorText = 'Kļūda pievienojot dokumentu';
        this.updateObjectSaveErrorText = 'Kļūda labojot dokumentu';
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;
        this.documentTypes$ = await this.documentTypesService
            .getAllDocumentTypes()
            .pipe(map((e: CommandResult<DocumentType[]>): DocumentType[] => e.data));
        this.loading = false;
    }

    createForm(): UntypedFormGroup {
        this.documentIsSignable = false;
        return this.fb.group({
            documentTypeId: [null, [Validators.required]],
            number: [null, [Validators.required, Validators.maxLength(32)]],
            date: [null],
            validUntil: [null],
            notes: [null, [Validators.maxLength(255)]],
            isGenerated: [false],
            isPrintableInContract: [false],
            isPublishableInPortal: [false],
            isPublishableInProfile: [false],
            isSignable: [false],
            contractId: [null],
            scontractId: [this.scontractId ?? null],
            workId: [null],
            crmEventId: [null],
            fuelReportId: [this.fuelReportId ?? null],
            payoutId: [null],
            performerId: [this.performerId ?? null],
        });
    }

    createAndFillForm(): UntypedFormGroup {
        this.documentIsSignable = this.originalObject.isSignable;
        return this.fb.group({
            documentTypeId: [this.originalObject.documentTypeId, [Validators.required]],
            number: [this.originalObject.number, [Validators.required, Validators.maxLength(32)]],
            date: [this.originalObject.date ? moment(this.originalObject.date).toDate() : null],
            validUntil: [this.originalObject.validUntil ? moment(this.originalObject.validUntil).toDate() : null],
            notes: [this.originalObject.notes, [Validators.maxLength(255)]],
            isGenerated: [this.originalObject.isGenerated],
            isPrintableInContract: [this.originalObject.isPrintableInContract],
            isPublishableInPortal: [this.originalObject.isPublishableInPortal],
            isPublishableInProfile: [this.originalObject.isPublishableInProfile],
            isSignable: [
                {
                    value: this.originalObject.isSignable,
                    disabled: this.originalObject.documentFiles.some((df: DocumentFile) => df.documentFileSigners.length > 0),
                },
            ],
            contractId: [this.originalObject.contractId],
            scontractId: [this.originalObject.scontractId],
            workId: [this.originalObject.workId],
            crmEventId: [this.originalObject.crmEventId],
            fuelReportId: [this.originalObject.fuelReportId],
            payoutId: [this.originalObject.payoutId],
            performerId: [this.originalObject.performerId],
        });
    }

    afterInit(): void {
        // set visible checkboxes array based on available data
        if (this.performerId || this.originalObject?.performerId) {
            this.visibleCheckboxes = ['isPublishableInProfile', 'isSignable'];
        } else if (this.scontractId || this.originalObject?.scontractId || this.contractId || this.originalObject?.contractId) {
            this.visibleCheckboxes = ['isGenerated', 'isPublishableInPortal', 'isSignable', 'isPrintableInContract'];
        } else {
            this.visibleCheckboxes = ['isSignable'];
        }

        this.objectForm.valueChanges.subscribe((values): void => {
            this.updateDocumetOwnershipInputVisibility(this.objectForm.value);
        });

        this.updateDocumetOwnershipInputVisibility(this.objectForm.value);
    }

    documentChanged(document: Document): void {
        if (!this.isEditMode) {
            return;
        }

        this.setEditMode(document);
    }

    async save(): Promise<void> {
        const document: Document = <Document>this.objectForm.getRawValue();
        if (this.isEditMode) {
            await this.service.update(
                this.objectId,
                this.originalObject ? Object.assign(this.originalObject, this.objectForm.getRawValue()) : this.objectForm.getRawValue(),
            );
            return;
        } else {
            if (this.scontractId) {
                document.scontractId = this.scontractId;
            }

            if (this.performerId) {
                document.performerId = this.performerId;
            }
        }
        this.objectId = await this.documentsService.create(document);
    }

    async validateAndSave(): Promise<void> {
        this.objectForm.statusChanges
            .pipe(
                filter((status) => status !== 'PENDING'),
                take(1),
            )
            .subscribe(async (status) => {
                if (status !== 'VALID') {
                    return;
                }
                try {
                    this.loading = true;
                    if (this.returnObject) {
                        const obj = this.objectForm.getRawValue();
                        obj.id = this.objectId;
                        this.submitAndReturn(obj);
                        return;
                    }
                    await this.save();
                    this.messageService.success(this.saveSuccessText);
                    const newDocument: Document = (await this.documentsService.getSingle(this.objectId)).data;
                    this.setEditMode(newDocument);

                    this.loading = false;
                    return;
                } catch (ex) {
                    this.loading = false;
                    if (ex.error?.code === ErrorCode.FieldValidation) {
                        this.fieldErrors = ex.error.errors;
                        this.mapErrorsToControls(ex.error.errors);
                    }

                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error(this.isEditMode ? this.updateObjectSaveErrorText : this.createObjectSaveErrorText);
                }
            });

        this.objectForm.markAsDirtyAndUpdateAll();
    }

    updateDocumetOwnershipInputVisibility(values): void {
        const relevantValues = this.documentOwnershipInputs.map((key) => ({ key, value: values[key] }));

        const filledFields = relevantValues.filter((f) => !!f.value);

        if (filledFields.length > 0 && filledFields[0].key == 'contractId') {
            this.visibleDocumentOwnershipInputs = [...filledFields.map((field) => field.key), 'payoutId'];
        } else if (filledFields.length > 0) {
            this.visibleDocumentOwnershipInputs = [...filledFields.map((field) => field.key)];
        } else {
            this.visibleDocumentOwnershipInputs = [...this.documentOwnershipInputs];
        }
    }

    beforeDocumentFileUpload = async (file: File) => {
        if (!this.isEditMode) {
            return;
        }

        const documentFileFormData = new FormData();

        documentFileFormData.append('documentId', this.originalObject.id.toString());
        documentFileFormData.append('file', file);

        try {
            this.loading = true;
            await this.documentsService.addDocumentFile(documentFileFormData);
            this.messageService.success('Dokumenta fails pievienots');
            await this.reloadData();
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda pievienojot dokumenta failu');
            return;
        }
    };

    submitAndReturn(val: boolean | Document): void {
        this.modal.close(val);
    }

    cancel(): void {
        this.modal.close();
    }

    performerToStringFn = (performer: Performer): string => this.performersService.toStringWithCompany(performer);

    customUploadRequest = (item: any): Subscription => {
        // Simulate successful upload
        const { onSuccess } = item;

        setTimeout(() => {
            // Notify NG-Zorro that the upload was successful
            if (onSuccess) {
                onSuccess({}, item.file, item.file);
            }
        });

        return new Subscription();
    };
}
